// API service for making HTTP requests
import {
  getAuthToken,
  refreshAccessToken,
  logout,
  getCurrentUser,
  ensureValidAccessToken,
} from "./auth";

const API_BASE_URL =
  process.env.REACT_APP_API_URL;
if (!process.env.REACT_APP_API_URL) {
  console.warn(
    "REACT_APP_API_URL environment variable is not set. API calls may fail."
  );
}

// Track refresh state to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;
let hasFailedRefresh = false; // Track if refresh has failed to prevent infinite loops

// Function to reset failed refresh state (call this after successful login)
export const resetFailedRefreshState = () => {
  hasFailedRefresh = false;
  isRefreshing = false;
  refreshPromise = null;
};

// Function to manually trigger token refresh (for testing)
export const forceTokenRefresh = async () => {
  try {
    const newToken = await refreshAccessToken();
    hasFailedRefresh = false;
    return newToken;
  } catch (error) {
    console.error("Manual token refresh failed:", error);
    throw error;
  }
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json, text/plain, */*",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const fetchWithTimeout = async (url, options, timeoutMs = 20000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isJwtExpiredMessage = (text) => {
  if (!text) return false;
  try {
    const parsed = JSON.parse(text);
    const message = parsed?.message || "";
    return message.includes("JWT expired");
  } catch {
    return text.includes("JWT expired");
  }
};

const runRefresh = async (context) => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  try {
    isRefreshing = true;
    refreshPromise = refreshAccessToken();
    const token = await refreshPromise;
    refreshPromise = null;
    hasFailedRefresh = false;
    console.info("Token refreshed", { context });
    return token;
  } catch (error) {
    refreshPromise = null;
    const message = error?.message || "";
    const isAuthError =
      message.includes("Session expired") ||
      message.includes("Refresh token is invalid") ||
      message.includes("Invalid refresh response") ||
      message.includes("invalid or expired");
    hasFailedRefresh = !!isAuthError;
    console.error("Token refresh failed", { context, message });
    throw error;
  } finally {
    isRefreshing = false;
  }
};

// Generic API request function with automatic token refresh
const apiRequest = async (
  endpoint,
  options = {},
  retry = true,
  onTokenRefresh
) => {
  const url = `${API_BASE_URL}${endpoint}`;
  let config = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options && options.headers ? options.headers : {}),
    },
  };

  if (config?.body instanceof FormData) {
    config.headers = { ...(config.headers || {}) };
    delete config.headers["Content-Type"];
  }

  try {
    const authRetryDelaysMs = [0, 300, 800, 1600];
    let lastError = null;
    let jwtRefreshAttempted = false;

    for (let attempt = 0; attempt < authRetryDelaysMs.length; attempt += 1) {
      const delay = authRetryDelaysMs[attempt];
      if (delay > 0) {
        await wait(delay);
      }

      const preflight = await ensureValidAccessToken(
        `api:${endpoint}:attempt:${attempt + 1}`
      );
      if (!preflight.ok) {
        if (
          preflight.reason === "no_tokens" ||
          preflight.reason === "refresh_failed_invalid"
        ) {
          try {
            logout();
          } catch { }
          throw new Error("Session expired. Please log in again.");
        }
      }

      config.headers = {
        ...getAuthHeaders(),
        ...(options && options.headers ? options.headers : {})
      };
      if (config?.body instanceof FormData) {
        delete config.headers["Content-Type"];
      }
      if (onTokenRefresh && preflight.reason === "refreshed") onTokenRefresh();

      let response = await fetchWithTimeout(url, config);
      let errorTextCache = null;

      if (response.ok) {
        const text = await response.text().catch(() => "");
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch {
          return { message: text };
        }
      }

      if (response.status === 400) {
        errorTextCache = await response.text().catch(() => "Unknown error");
        if (isJwtExpiredMessage(errorTextCache) && !jwtRefreshAttempted) {
          try {
            await runRefresh(`api:${endpoint}:jwt-expired`);
            jwtRefreshAttempted = true;
          } catch (refreshError) {
            const message = refreshError?.message || "";
            if (
              message.includes("Session expired") ||
              message.includes("Refresh token is invalid") ||
              message.includes("Invalid refresh response") ||
              message.includes("invalid or expired")
            ) {
              try {
                logout();
              } catch { }
              throw new Error("Session expired. Please log in again.");
            }
            lastError = refreshError;
            continue;
          }
          continue;
        }
      }

      if (response.status === 401) {
        try {
          await runRefresh(`api:${endpoint}:unauthorized`);
          continue;
        } catch (refreshError) {
          const message = refreshError?.message || "";
          if (
            message.includes("Session expired") ||
            message.includes("Refresh token is invalid") ||
            message.includes("Invalid refresh response") ||
            message.includes("invalid or expired")
          ) {
            try {
              logout();
            } catch { }
            throw new Error("Session expired. Please log in again.");
          }
          lastError = refreshError;
          continue;
        }
      }

      const errorText =
        errorTextCache ?? (await response.text().catch(() => "Unknown error"));
      const errorMessage =
        errorText && errorText !== "Unknown error"
          ? `API request failed: ${response.status} - ${errorText}`
          : `API request failed: ${response.status}`;
      console.error(errorMessage);

      if (response.status === 403) {
        console.error("403 Forbidden - User may not have required permissions");
        console.error("Current user info:", getCurrentUser());
        throw new Error(
          "Access denied. You may not have permission to perform this action."
        );
      }

      throw new Error(errorMessage);
    }

    if (lastError) throw lastError;
    throw new Error("Request failed after repeated attempts. Please try again.");
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

// Order-related API calls
export const createOrder = async (orderData) => {
  return apiRequest("/orders", {
    method: "POST",
    body: JSON.stringify(orderData),
  });
};

export const getOrders = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const endpoint = `/orders${queryParams ? `?${queryParams}` : ""}`;
  return apiRequest(endpoint);
};

export const getPendingOrders = async () => {
  return apiRequest("/orders?status=pending");
};

export const getOrderById = async (orderId) => {
  return apiRequest(`/orders/${orderId}`);
};

// Update order details (items and amounts)
export const updateOrderDetails = async (orderId, details) => {
  return apiRequest(`/orders/${orderId}/details`, {
    method: "PUT",
    body: JSON.stringify(details),
  });
};

export const updateOrderStatus = async (orderId, status) => {
  return apiRequest(`/orders/${orderId}`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
};

export const updateManagementOrderStatus = async (orderId, status) => {
  return apiRequest(`/orders/${orderId}`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
};

export const getPendingOrderById = async (orderId) => {
  return apiRequest(`/orders/${orderId}?status=pending`);
};

export const cancelOrder = async (orderId) => {
  return apiRequest(`/orders/${orderId}/cancel`, {
    method: "PUT",
  });
};

// Attachments
export const getOrderAttachments = async (orderId) => {
  return apiRequest(`/orders/${orderId}/attachments`, {
    method: "GET",
  });
};

// Account-related API calls
export const getAccountInfo = async () => {
  return apiRequest("/auth/account");
};

// Help-related API calls
export const submitBugReport = async (bugReport) => {
  return apiRequest("/help/bug-report", {
    method: "POST",
    body: JSON.stringify(bugReport),
  });
};

// Fetch clients associated with the logged-in dealer
export const getClients = async () => {
  return apiRequest("/users/clients");
};

// Fetch wholesaler by ID
export const getWholesalerById = async (id) => {
  return apiRequest(`/wholesalers/${id}`);
};

// Fetch a client by ID
export const getClientById = async (clientId) => {
  return apiRequest(`/clients/${clientId}`);
};

// Create a new client
export const createClient = async (clientData) => {
  return apiRequest("/clients/add-client", {
    method: "POST",
    body: JSON.stringify(clientData),
  });
};

// Fetch all clients
export const getAllClients = async () => {
  return apiRequest("/clients");
};

export const getDenominations = async () => {
  return apiRequest("/denominations", {
    method: "GET",
  });
};

// Mock API functions for development
export const mockApi = {
  createOrder: async (orderData) => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return {
      id: `ORD-${Date.now()}`,
      ...orderData,
      status: "pending",
      createdAt: new Date().toISOString(),
      merchantName: orderData.merchantName || "Test Wholesaler Created",
    };
  },

  getOrders: async (filters = {}) => {
    await new Promise((resolve) => setTimeout(resolve, 800));
    const mockOrders = [
      {
        id: "ORD-001",
        type: "E-PIN",
        total: 150,
        status: "completed",
        createdAt: "2024-01-15T10:30:00Z",
        items: { 10: 5, 20: 3, 50: 1 },
        merchantName: "Test Wholesaler A",
      },
      {
        id: "ORD-002",
        type: "Scratch",
        total: 300,
        status: "processing",
        createdAt: "2024-01-15T09:15:00Z",
        items: { A: 2, B: 1 },
        merchantName: "Test Wholesaler B",
      },
      {
        id: "ORD-003",
        type: "E-TopUp",
        total: 60,
        status: "pending",
        createdAt: "2024-01-15T08:45:00Z",
        items: { 10: 2, 20: 2 },
        merchantName: "Test Wholesaler C",
      },
    ];

    if (filters.status) {
      return mockOrders.filter((order) => order.status === filters.status);
    }

    return mockOrders;
  },

  getAccountInfo: async () => {
    await new Promise((resolve) => setTimeout(resolve, 600));
    return {
      dealerId: "DEALER-001",
      dealerName: "ABC Electronics Store",
      contactPerson: "John Smith",
      email: "john.smith@abcelectronics.com",
      phone: "+1 (555) 123-4567",
      address: "123 Main Street, City, State 12345",
      accountType: "Premium",
      registrationDate: "2023-01-15",
      status: "Active",
      creditLimit: 10000,
      currentBalance: 2500,
      totalOrders: 156,
      completedOrders: 142,
      pendingOrders: 14,
    };
  },

  submitBugReport: async (bugReport) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      id: `BUG-${Date.now()}`,
      ...bugReport,
      status: "submitted",
      submittedAt: new Date().toISOString(),
    };
  },
};

export const getManagementPendingOrders = async ({
  page = 1,
  size = 20,
  status = "Pending",
  startDate,
  endDate,
} = {}) => {
  let endpoint = `/orders/all-dealers?status=${status}&page=${page}&size=${size}`;
  if (startDate) endpoint += `&startDate=${startDate}`;
  if (endDate) endpoint += `&endDate=${endDate}`;
  return apiRequest(endpoint);
};

export const getManagementScratchOrders = async ({
  page = 1,
  size = 20,
} = {}) => {
  // Use the correct endpoint for scratch orders that are not delivered
  const endpoint = `/orders/scratch/not-delivered?page=${page}&size=${size}`;

  try {
    const result = await apiRequest(endpoint);

    // Handle different response structures
    if (Array.isArray(result)) {
      // If response is directly an array
      return {
        content: result,
        totalElements: result.length,
        totalPages: 1,
        size: result.length,
        number: 0,
      };
    } else if (result && (result.content || result.orders)) {
      // If response has content or orders property
      return result;
    } else if (result && typeof result === "object") {
      // If response is an object but doesn't have expected properties, check if it's a single order
      return {
        content: [result],
        totalElements: 1,
        totalPages: 1,
        size: 1,
        number: 0,
      };
    } else {
      // If response is empty or unexpected format
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0,
      };
    }
  } catch (error) {
    console.error("Failed to fetch scratch orders:", error);
    throw error;
  }
};

// Fetch a single order by ID for all dealers (status=Pending or as needed)
export const getManagementOrderById = async (orderId, status = "Pending") => {
  return apiRequest(`/orders/all-dealers/${orderId}?status=${status}`);
};

// Test function to check if user has management permissions
export const testManagementAccess = async () => {
  try {
    await apiRequest("/orders/all-dealers?page=0&size=1");
    return true;
  } catch (error) {
    console.error("Management access test failed:", error);
    return false;
  }
};

// Upload attachments to an order
export const uploadOrderAttachments = async (orderId, files) => {
  const uploadSingle = async (file) => {
    const formData = new FormData();
    formData.append("files", file);
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const res = await fetch(`${API_BASE_URL}/orders/${orderId}/attachments`, {
      method: "POST",
      body: formData,
      headers,
      credentials: "include",
    });
    if (!res.ok) {
      const contentType = res.headers.get("content-type") || "";
      let serverMessage = "";
      if (contentType.includes("application/json")) {
        const json = await res.json().catch(() => ({}));
        serverMessage = json?.message || JSON.stringify(json);
      } else {
        serverMessage = await res.text();
      }
      const err = new Error(
        `Failed to upload attachments: ${res.status} ${res.statusText}${serverMessage ? ` - ${serverMessage}` : ""
        }`
      );
      err.status = res.status;
      err.serverMessage = serverMessage;
      throw err;
    }
    try {
      return await res.json();
    } catch {
      return {};
    }
  };

  const results = [];
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    const out = await uploadSingle(file);
    results.push(out);
  }
  return results;
};

// Test API connectivity
export const testApiConnectivity = async () => {
  try {
    // Try a simple endpoint that should exist
    const response = await fetch(`${API_BASE_URL}/orders?page=0&size=1`, {
      method: "GET",
    });
    return { success: true, status: response.status };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Split a scratch order into multiple quantities
export const splitScratchOrder = async (orderLineId, splits) => {
  console.log("splitScratchOrder called with:", {
    orderLineId,
    splits,
    url: `${API_BASE_URL}/orders/scratch/split`,
    apiBaseUrl: API_BASE_URL,
    hasToken: !!getAuthToken(),
  });

  try {
    const result = await apiRequest("/orders/scratch/split", {
      method: "POST",
      body: JSON.stringify({
        orderLineId: parseInt(orderLineId),
        scratchOrderId: parseInt(orderLineId),
        splits,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    console.log("splitScratchOrder success:", result);
    return result;
  } catch (error) {
    console.error("splitScratchOrder error:", {
      error,
      message: error.message,
      stack: error.stack,
      orderLineId,
      splits,
      apiBaseUrl: API_BASE_URL,
    });

    // Provide more specific error messages
    let errorMessage = "Failed to split scratch order";
    if (error.message.includes("Failed to fetch")) {
      errorMessage =
        "Network error - please check your connection and API server status";
    } else if (error.message.includes("404")) {
      errorMessage = "Scratch order not found - it may have been deleted";
    } else if (error.message.includes("401") || error.message.includes("403")) {
      errorMessage =
        "Authentication error - please refresh the page and try again";
    } else if (error.message.includes("500")) {
      errorMessage = "Server error - please try again later";
    } else {
      errorMessage = `Failed to split: ${error.message}`;
    }

    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Update individual scratch order fields (from, to, subStatus)
export const updateScratchOrder = async (scratchOrderId, updateData) => {
  // Debug logging to help identify the issue
  console.log("updateScratchOrder called with:", {
    scratchOrderId,
    updateData,
    url: `${API_BASE_URL}/orders/scratch/${scratchOrderId}`,
    apiBaseUrl: API_BASE_URL,
    hasToken: !!getAuthToken(),
  });

  try {
    const result = await apiRequest(`/orders/scratch/${scratchOrderId}`, {
      method: "PATCH",
      body: JSON.stringify(updateData),
      headers: {
        "Content-Type": "application/json",
      },
    });
    console.log("updateScratchOrder success:", result);
    return result;
  } catch (error) {
    console.error("updateScratchOrder error:", {
      error,
      message: error.message,
      stack: error.stack,
      scratchOrderId,
      updateData,
      apiBaseUrl: API_BASE_URL,
    });

    // Provide more specific error messages
    let errorMessage = "Failed to update scratch order";
    if (error.message.includes("Failed to fetch")) {
      errorMessage =
        "Network error - please check your connection and API server status";
    } else if (error.message.includes("404")) {
      errorMessage = "Scratch order not found - it may have been deleted";
    } else if (error.message.includes("401") || error.message.includes("403")) {
      errorMessage =
        "Authentication error - please refresh the page and try again";
    } else if (error.message.includes("500")) {
      errorMessage = "Server error - please try again later";
    } else {
      errorMessage = `Failed to update: ${error.message}`;
    }

    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};// Dashboard - fetch order trend data for overview
export const getDashboardOrdersTrend = async (from, to, groupBy = 'week', type = '') => {
  const params = new URLSearchParams({ from, to, groupBy });
  if (type) params.append('type', type);
  return apiRequest(`/dashboard/orders/trend?${params.toString()}`);
};

// Employee Scratch Orders Management
export const getEmployeeScratchOrders = async () => {
  const endpoint = `/orders/employee/scratch/not-delivered`;
  try {
    const result = await apiRequest(endpoint);
    // Standardize response format like getManagementScratchOrders
    if (Array.isArray(result)) {
      return {
        content: result,
        totalElements: result.length,
        totalPages: 1,
        size: result.length,
        number: 0,
      };
    } else if (result && (result.content || result.orders)) {
      return result;
    } else if (result && typeof result === "object") {
      return {
        content: [result],
        totalElements: 1,
        totalPages: 1,
        size: 1,
        number: 0,
      };
    } else {
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0,
      };
    }
  } catch (error) {
    console.error("Failed to fetch employee scratch orders:", error);
    throw error;
  }
};

export const updateEmployeeScratchOrder = async (scratchOrderId, updateData) => {
  return apiRequest(`/orders/employee/scratch/${scratchOrderId}`, {
    method: "PATCH",
    body: JSON.stringify(updateData),
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const splitEmployeeScratchOrder = async (orderLineId, splits) => {
  return apiRequest("/orders/employee/scratch/split", {
    method: "POST",
    body: JSON.stringify({
      orderLineId: parseInt(orderLineId),
      splits,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
};
